import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTeamSectionStrapi, useTeamMembers } from '../hooks/useTeamSectionStrapi';
import { ArrowLeft, ArrowRight } from './icons';
import useEmblaCarousel from 'embla-carousel-react';
import { getCurrentLocale } from '../services/api';
import { getBestImageUrl } from '../utils/imageUrl';

interface StrapiImage {
  data?: {
    id: number;
    attributes: {
      url: string;
      alternativeText?: string;
    };
  };
}

interface DefaultTeamMember {
  name: string;
  jobTitle: string;
  image: string;
}

interface StrapiTeamMember {
  id: number;
  documentId: string;
  name: string;
  jobTitle: string;
  image?: {
    id: number;
    url: string;
    alternativeText?: string;
    width: number;
    height: number;
    formats?: {
      thumbnail?: { url: string; width: number; height: number; };
      small?: { url: string; width: number; height: number; };
      medium?: { url: string; width: number; height: number; };
      large?: { url: string; width: number; height: number; };
    };
  };
  bio?: string;
  order?: number;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

type TeamMember = DefaultTeamMember | StrapiTeamMember;

const TeamSection: React.FC = () => {
  // Primary data source: team-member collection
  const locale = getCurrentLocale();
  const isRTL = locale === 'ar';
  const { data: teamMembersData, loading: teamMembersLoading, error: teamMembersError } = useTeamMembers();
  
  // Optional: team-section for settings (title, background color, etc.)
  const { data: strapiData, loading: strapiLoading, error: strapiError } = useTeamSectionStrapi();

  // Debug logging for team members collection (primary data)
  if (teamMembersData && !teamMembersLoading && !teamMembersError) {
    console.log('🎯 TeamMembers - ✅ Collection data loaded:', !!teamMembersData);
    console.log('🎯 TeamMembers - Count:', teamMembersData.length);
    console.log('🎯 TeamMembers - First member:', teamMembersData[0]);
    console.log('🎯 TeamMembers - All members:', teamMembersData);
  }

  // Debug logging for team section settings (optional)
  if (strapiData && !strapiLoading && !strapiError) {
    console.log('🎯 TeamSection - ✅ Settings loaded:', !!strapiData);
    console.log('🎯 TeamSection - Title:', strapiData.title);
    console.log('🎯 TeamSection - Background color:', strapiData.backgroundColor);
  }
  const [emblaRef, emblaApi] = useEmblaCarousel({
    direction: isRTL ? 'rtl' : 'ltr',
    align: 'start',
    loop: false,
    skipSnaps: false,
    dragFree: false,
    containScroll: 'trimSnaps',
    slidesToScroll: 1
  });

  // Re-initialize carousel when RTL changes so alignment updates correctly
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit({
      direction: isRTL ? 'rtl' : 'ltr',
      align: 'start',
      loop: false,
      skipSnaps: false,
      dragFree: false,
      containScroll: 'trimSnaps',
      slidesToScroll: 1,
    });
  }, [emblaApi, isRTL]);

  // Default content when no Strapi data is available
  const defaultTeamMembers: DefaultTeamMember[] = [
    {
      name: "Drs. Marloes van Dijk",
      jobTitle: "Tricholoog & Hoofdhuidexpert",
      image: "/uploads/over-ons/teammember.png"
    },
    {
      name: "Tom Jansen",
      jobTitle: "Tricholoog",
      image: "/uploads/over-ons/teammember.png"
    },
    {
      name: "Sophie de Graaf",
      jobTitle: "Nazorgspecialist",
      image: "/uploads/over-ons/teammember.png"
    },
    {
      name: "Rick BAI",
      jobTitle: "Tricholoog & Hoofdhuidexpert",
      image: "/uploads/over-ons/teammember.png"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.3, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  // Navigation functions
  const scrollPrev = () => {
    if (emblaApi) emblaApi.scrollPrev();
  };

  const scrollNext = () => {
    if (emblaApi) emblaApi.scrollNext();
  };

  // Check if we can scroll further
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };

    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  // Helper function to check if member has Strapi image
  const hasStrapiImage = (member: TeamMember): member is StrapiTeamMember => {
    return 'id' in member && 'image' in member && typeof member.image === 'object' && !!member.image?.url;
  };

  // Helper function to generate placeholder SVG with initials
  const generatePlaceholderSvg = (name: string): string => {
    const initials = name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2);
    
    const backgroundColor = 'rgb(10, 56, 64)';
    const textColor = 'white';
    
    const svg = `
      <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
        <rect width="200" height="200" fill="${backgroundColor}"/>
        <text x="100" y="120" font-family="Arial, sans-serif" font-size="60" font-weight="bold" 
              text-anchor="middle" fill="${textColor}">${initials}</text>
      </svg>
    `;
    
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  // Helper function to get image URL using getBestImageUrl
  const getMemberImageUrl = (member: TeamMember): string => {
    if (hasStrapiImage(member)) {
      // Use getBestImageUrl for better image quality selection from flat Strapi structure
      return getBestImageUrl(member.image);
    } else if ('image' in member && typeof member.image === 'string') {
      // Handle default team members with string image paths
      return member.image;
    } else {
      // Generate a personalized placeholder with initials
      return generatePlaceholderSvg(member.name);
    }
  };

  if (teamMembersLoading) {
    return (
      <section className="team-section team-section-loading" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        <div className="team-section-loading-container">
          <div className="loading-placeholder">
            <div className="loading-title"></div>
            <div className="loading-text">Loading team members...</div>
          </div>
        </div>
      </section>
    );
  }

  if (teamMembersError) {
    return (
      <section className="team-section team-section-error" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        <div className="team-section-error-container">
          <div className="team-section-header">
            <h2 className="team-section-title">
              Het team
            </h2>
            <div className="team-section-navigation">
              <button
                onClick={scrollPrev}
                disabled={!canScrollPrev}
                className="team-section-nav-button"
              >
                <ArrowLeft size={24} />
              </button>
              <button
                onClick={scrollNext}
                disabled={!canScrollNext}
                className="team-section-nav-button"
              >
                <ArrowRight size={24} />
              </button>
            </div>
          </div>
          <div className="team-section-carousel-container">
            <div ref={emblaRef} className="team-section-carousel">
              <div className="team-section-carousel-slides">
                {defaultTeamMembers.map((member, index) => (
                  <div 
                    key={index} 
                    className="team-member-card"
                  >
                    <div className="team-member-image">
                      <img 
                        src={getMemberImageUrl(member)} 
                        alt={member.name} 
                        className="team-member-img"
                      />
                    </div>
                    <h3 className="team-member-name">
                      {member.name}
                    </h3>
                    <p className="team-member-job-title">
                      {member.jobTitle}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Priority: team-members collection (primary) > team-section settings > default
  const teamMembers = teamMembersData || defaultTeamMembers;
  
  // Get section settings from team-section (title, background) or use defaults
  const sectionTitle = strapiData?.title || "Het team";
  const backgroundColor = strapiData?.backgroundColor || "#E0EBE8";
  
  console.log('🎯 TeamSection - Using data source:');
  console.log('   - Team members:', teamMembersData ? 'Strapi Collection' : 'Default');
  console.log('   - Section settings:', strapiData ? 'Strapi Section' : 'Default');
  console.log('   - Final team members count:', teamMembers.length);

  return (
    <motion.section
      className="team-section team-section-enhanced"
      style={{ background: backgroundColor }}
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className="team-section-container">
        <div className="team-section-header">
          <h2 className="team-section-title">
            {sectionTitle}
          </h2>
          <div className="team-section-navigation">
            <button
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              className="team-section-nav-button"
            >
              <ArrowLeft size={24} />
            </button>
            <button
              onClick={scrollNext}
              disabled={!canScrollNext}
              className="team-section-nav-button"
            >
              <ArrowRight size={24} />
            </button>
          </div>
        </div>
        
        <div className="team-section-carousel-container">
          <div ref={emblaRef} className="team-section-carousel">
            <div className="team-section-carousel-slides">
              {teamMembers.map((member, index) => {
                const image = getMemberImageUrl(member);
                
                return (
                  <motion.div 
                    key={hasStrapiImage(member) ? member.documentId || member.id : index}
                    className="team-member-card"
                    variants={itemVariants}
                  >
                    <img 
                      src={image} 
                      alt={hasStrapiImage(member) ? (member.image?.alternativeText || member.name) : member.name}
                      className="team-member-image"
                      onError={(e) => {
                        // If image fails to load, replace with placeholder
                        const target = e.target as HTMLImageElement;
                        if (!target.src.startsWith('data:image/svg+xml')) {
                          target.src = generatePlaceholderSvg(member.name);
                        }
                      }}
                    />
                    <h3 className="team-member-name">
                      {member.name}
                    </h3>
                    <p className="team-member-job-title">
                      {member.jobTitle}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default TeamSection; 